import * as Popover from '@radix-ui/react-popover';
import { cn } from '../../lib/cn';
import {
  ACRONYM_TO_SCALE,
  formatSigned,
  getScaleMax,
  SCALE_MIN,
  type ScaleAcronym,
} from '../../utils/scoreColor';

export interface ScaleColumnFooterProps {
  acronym: ScaleAcronym;
  primaryName: 'Truth' | 'Engagement' | 'Integration';
  fullName: string;
  score: number;
  emphasized?: boolean;
}

function scaleRangeLabel(acronym: ScaleAcronym): string {
  const key = ACRONYM_TO_SCALE[acronym];
  const min = SCALE_MIN[key];
  const max = getScaleMax(acronym);
  return `Valid range ${min} to +${max}; displayed as score out of ${max}`;
}

export function ScaleColumnFooter({
  acronym,
  primaryName,
  fullName,
  score,
  emphasized = false,
}: ScaleColumnFooterProps) {
  // -- Emphasize Truth / Engagement / Integration in the footer title
  const nameParts = fullName.split(primaryName);
  const footerTitle =
    nameParts.length === 2 ? (
      <>
        {nameParts[0]}
        <span className="font-black text-atlas-white">{primaryName}</span>
        {nameParts[1]}
      </>
    ) : (
      fullName
    );

  return (
    <div
      className={cn(
        'mt-1 flex flex-col items-center rounded-md border border-atlas-border/70 bg-atlas-deep/80 px-3 py-3 text-center shadow-atlas-ladder',
        'night:border-purple-400/40 night:bg-[#1a0a30]/90',
        emphasized && 'border-atlas-bloom/50 shadow-atlas-ladder-active'
      )}
    >
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(
              'font-display text-2xl font-black tracking-wide text-atlas-white transition-colors',
              'hover:text-atlas-bloom',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atlas-bloom focus-visible:ring-offset-2 focus-visible:ring-offset-atlas-void',
              emphasized ? 'sm:text-3xl' : 'sm:text-2xl'
            )}
            aria-label={`${fullName} (${acronym}), score ${formatSigned(score)}`}
          >
            {acronym}{' '}
            <span
              className={cn(
                'tabular-nums',
                score < 0
                  ? 'text-red-400'
                  : score === 0
                    ? 'text-atlas-muted'
                    : 'text-atlas-bloom'
              )}
            >
              {formatSigned(score)}
            </span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-[100] max-w-xs rounded-atlas-panel border border-atlas-border bg-atlas-deep p-3 text-sm leading-relaxed text-atlas-muted shadow-atlas-glow-md"
            sideOffset={8}
          >
            <p className="font-mono text-xs font-bold uppercase tracking-wider text-atlas-vivid">
              {primaryName} · {acronym}
            </p>
            <p className="mt-1 text-atlas-bright-text">{fullName}</p>
            <p className="mt-2 text-xs text-atlas-dim">
              {scaleRangeLabel(acronym)}
            </p>
            <Popover.Arrow className="fill-atlas-border" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <p className="mt-1 text-xs font-semibold text-atlas-muted sm:text-sm">
        {footerTitle}
      </p>
      {emphasized ? (
        <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-atlas-label">
          Primary Scale
        </p>
      ) : null}
    </div>
  );
}
